import LiveAgentsAvatarGroup from "@/components/call-center/live.agents.avatar.group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCallCenters } from "@/hooks/call-center/useCallCenters";
import { useLiveAgents } from "@/hooks/call-center/useLiveAgents";
import { useAppDispatch } from "@/hooks/redux-hooks";
import useAuthUser from "@/hooks/useAuthUser";
import { cn } from "@/lib/utils";
import { PATH_CALL_CENTER, PATH_CALL_CENTER_CLOUD_RECORDINGS, PATH_CALL_CENTER_COMPANY_ROOM, PATH_CALL_CENTER_EOD_REPORT, PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS } from "@/routes/paths";
import { getAllUsers } from "@/store/reducers/users.reducer";
import { Building2, DoorOpen, FileText, Headphones, LayoutDashboard, Mic } from "lucide-react";
import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { StatusDropdown } from "./status.dropdown";

// Visuals ported from demo-ui CallCenterLayout.tsx ("Live workspace" sub-header
// + pill tabs). The demo animates the active pill with framer-motion; here the
// active state is static styling.
const tabClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    "inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-1.5 text-[12.5px] font-medium transition whitespace-nowrap",
    isActive
      ? "border-white/[0.08] bg-gradient-to-b from-white/[0.08] to-white/[0.02] text-white shadow-[0_8px_24px_-12px_rgba(34,197,94,0.55)]"
      : "border-transparent text-slate-400 hover:text-white"
  );

export default function MainCallCenterTopHeader() {
  const dispatch = useAppDispatch();
  const { user, hasPermission } = useAuthUser();
  const canSubmitEodReport = (user?.assignedCallCenterIds?.length ?? 0) > 0;
  const canAccessRecordings = hasPermission("recordings:access");
  const { callCenters, selectedCallCenter, selectionReady, selectCallCenter } = useCallCenters();
  const isAssignedToSelectedCallCenter =
    !!selectedCallCenter && (user?.assignedCallCenterIds ?? []).includes(selectedCallCenter.id);
  const activeCallCenters = callCenters.filter((cc) => cc.isCallCenterActive);
  const { total, liveAgents, loading } = useLiveAgents(selectionReady ? selectedCallCenter?.id : undefined);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  return (
    <header className="flex w-full flex-col gap-4 p-4 md:px-6">
      {/* Sub-header: live workspace identity + room selector | status + agents */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex w-full items-center justify-between md:w-auto">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-accent-green/25 to-emerald-700/10 ring-1 ring-accent-green/30">
              <Headphones className="h-4 w-4 text-accent-green" />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inset-0 animate-ping rounded-full bg-accent-green" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-green" />
                </span>
                Live workspace
              </span>
              <h1 className="mt-0.5 whitespace-nowrap text-[22px] font-semibold leading-none tracking-tight text-white">
                Call Center
              </h1>
            </div>
            {/* With a single room there's nothing to choose — hide the selector */}
            {activeCallCenters.length > 1 && (
              <Select
                value={selectedCallCenter ? String(selectedCallCenter.id) : ""}
                onValueChange={(val) => {
                  const cc = activeCallCenters.find((c) => String(c.id) === val) ?? null;
                  selectCallCenter(cc);
                }}
              >
                <SelectTrigger className="h-9 w-auto gap-1.5 rounded-xl border-white/[0.06] bg-white/[0.02] px-3 text-[12px] font-medium text-slate-200 transition hover:border-white/[0.14] hover:bg-white/[0.04] focus:ring-0 focus:ring-offset-0">
                  <span className="text-slate-400">Room:</span>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {activeCallCenters.map((cc) => (
                    <SelectItem key={cc.id} value={String(cc.id)}>
                      {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex w-full items-center gap-2 md:w-auto md:gap-3">
          {isAssignedToSelectedCallCenter && <StatusDropdown />}
          <LiveAgentsAvatarGroup total={total} liveAgents={liveAgents} loading={loading} />
        </div>
      </div>

      {/* Top tabs */}
      <nav className="inline-flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-white/[0.06] bg-white/[0.02] p-1 backdrop-blur">
        <NavLink to={PATH_CALL_CENTER} end className={tabClassName}>
          <LayoutDashboard className="h-3.5 w-3.5" />
          Dashboard
        </NavLink>
        {canAccessRecordings && (
          <NavLink to={PATH_CALL_CENTER_CLOUD_RECORDINGS} className={tabClassName}>
            <Mic className="h-3.5 w-3.5" />
            Recordings
          </NavLink>
        )}
        {canAccessRecordings && (
          <NavLink to={PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS} className={tabClassName}>
            <DoorOpen className="h-3.5 w-3.5" />
            Personal Rooms
          </NavLink>
        )}
        {canSubmitEodReport && (
          <NavLink to={PATH_CALL_CENTER_EOD_REPORT} className={tabClassName}>
            <FileText className="h-3.5 w-3.5" />
            EOD Report
          </NavLink>
        )}
        <NavLink to={PATH_CALL_CENTER_COMPANY_ROOM} className={tabClassName}>
          <Building2 className="h-3.5 w-3.5" />
          Company Room
        </NavLink>
      </nav>
    </header>
  );
}
